// shipCourse.ts — Shared client-side ship geometry: a planet's position in the parent-star
// frame, the system rim (where hyperlane gates sit), and the galaxy-map position of a ship
// crossing a lane. The in-system FLIGHT PATH itself is no longer a descriptor arc — it is the
// physics prediction in shipPhysics.ts, where the client mirrors the server's flip-and-burn
// N-body integrator to evolve each ship between the coarse server snapshots.
import { Vector3 } from 'three';
import type { Galaxy, Planet, StarSystem } from '../types';
import type { Ship } from '../state/economy';
import { orbitPosition } from './orbit';

/** Planet position in the parent-star frame (orbital-plane inclination applied). */
export function planetLocalPos(p: Planet, time: number, out: Vector3): Vector3 {
  orbitPosition(p, time, out);
  const inc = p.inclination;
  const z = out.z;
  return out.set(out.x, -z * Math.sin(inc), z * Math.cos(inc));
}

export function systemById(galaxy: Galaxy, id: string): StarSystem | undefined {
  return galaxy.systems.find((s) => s.id === id);
}

// Radius at which hyperlane gates sit — mirrors systemRim in server/sim/ship.go.
export function systemRim(s: StarSystem): number {
  let max = s.radius * 2;
  for (const p of s.planets) {
    const r = p.semiMajorAxis * (1 + p.eccentricity) + p.radius;
    if (r > max) max = r;
  }
  return max * 1.3;
}

function progress(ship: Ship, time: number): number {
  const d = ship.arriveTime - ship.departTime;
  const pr = d > 0 ? (time - ship.departTime) / d : 0;
  return pr < 0 ? 0 : pr > 1 ? 1 : pr;
}

/** Write the galaxy-space position of a ship crossing a hyperlane (a straight lerp between
 *  the two systems on the galaxy map) at `time`. Returns false if the endpoints can't resolve. */
export function hyperlaneGalaxyPos(galaxy: Galaxy, ship: Ship, time: number, out: Vector3): boolean {
  const a = systemById(galaxy, ship.originId);
  const b = systemById(galaxy, ship.destId);
  if (!a || !b) return false;
  const pr = progress(ship, time);
  out.set(
    a.position[0] + (b.position[0] - a.position[0]) * pr,
    a.position[1] + (b.position[1] - a.position[1]) * pr,
    a.position[2] + (b.position[2] - a.position[2]) * pr,
  );
  return true;
}
